<template>
  <div>
    <sc-card class="sc-vehicle-reg-card sc-box-shadow" v-if="editedVehicleDetails">
      <div slot="title">
        <div class="sc-vehicle-reg-card-header-container">
          <div class="sc-vehicle-reg-card-title-and-icon">
            <i
              :class="VehicleIcons[editedVehicleDetails.groupCode]"
              class="sc-vehicle-reg-vehicle-icon">
            </i>
            <div class="sc-vehicle-reg-card-title">
              <div
                v-if="isFilled()"
                class="sc-vehicle-reg-make-type-licence">
                {{ $t('vehicle_registration.make_type_licence_plate', {
                  vehicleBrand: vehicleDetails.vehicleBrand ? vehicleDetails.vehicleBrand : $t('vehicle_registration.missing_vehicle_data'),
                  vehicleType: vehicleDetails.vehicleType ? vehicleDetails.vehicleType : $t('vehicle_registration.missing_vehicle_data'),
                  licencePlate: vehicleDetails.licencePlate ? vehicleDetails.licencePlate : $t('vehicle_registration.missing_vehicle_licence_plate') })
                }}
              </div>
              <div v-else>
                {{ $t('vehicle_registration.position_vehicle_type', {
                  position: getPositionOfVehicleInGroup(editedVehicleDetails),
                  vehicleType: $t(VehicleGroupLabels[editedVehicleDetails.groupCode]) })
                }}
              </div>
            </div>
          </div>
          <i @click="onClickClose" class="sc-icon-cancel"></i>
        </div>
        <hr class="sc-vehicle-reg-upper-hr-line">
      </div>
      <div slot="content" class="card-content">
        <sc-form-input :class="validationClasses('productionYear')">
          <label class="sc-vehicle-reg-input-label" for="vehicle-year">{{ $t('vehicle_registration.creation_year') }}</label>
          <div class="form-element-wrapper">
            <input
              type="text"
              name="productionYear"
              maxlength="4"
              :placeholder="$t('vehicle_registration.year_placeholder')"
              v-validate.immediate="'numeric|min:4|max:4|min_value:1900|max_value:' + currentYear"
              v-model="editedVehicleDetails.productionYear">
          </div>
          <div class="error-message" v-if="isTouched('productionYear')">{{ errors.first('productionYear') }}</div>
        </sc-form-input>

        <sc-form-input :class="validationClasses('vehicleBrand')">
          <label class="sc-vehicle-reg-input-label" for="vehicle-brand">{{ $t('vehicle_registration.vehicle_make') }}</label>
          <div class="form-element-wrapper">
            <input
              name="vehicleBrand"
              type="text"
              maxlength="255"
              :placeholder="$t('vehicle_registration.brand_placeholder')"
              v-validate.immediate="'min:1'"
              v-model="editedVehicleDetails.vehicleBrand">
          </div>
          <div class="error-message" v-if="isTouched('vehicleBrand')">{{ errors.first('vehicleBrand') }}</div>
        </sc-form-input>

        <sc-form-input :class="validationClasses('vehicleType')">
          <label class="sc-vehicle-reg-input-label" for="vehicle-type">{{ $t('vehicle_registration.vehicle_type') }}</label>
          <div class="form-element-wrapper">
            <input
              name="vehicleType"
              type="text"
              maxlength="255"
              :placeholder="$t('vehicle_registration.type_placeholder')"
              v-validate.immediate="'min:1'"
              v-model="editedVehicleDetails.vehicleType">
          </div>
          <div class="error-message" v-if="isTouched('vehicleType')">{{ errors.first('vehicleType') }}</div>
        </sc-form-input>

        <sc-form-input :class="validationClasses('licencePlate')">
          <label class="sc-vehicle-reg-input-label" for="vehicle-plate">{{ $t('vehicle_registration.vehicle_licence_plate') }}</label>
          <div class="form-element-wrapper">
            <input
              name="licencePlate"
              type="text"
              :placeholder="$t('vehicle_registration.licence_plate_placeholder')"
              maxlength="8"
              v-validate.immediate="'max:8'"
              v-model="editedVehicleDetails.licencePlate">
          </div>
          <div class="error-message" v-if="isTouched('licencePlate')">{{ errors.first('licencePlate') }}</div>
        </sc-form-input>

        <hr class="sc-vehicle-reg-bottom-hr-line">

        <sc-form-input v-if="packageOptions.length > 0">
          <label class="sc-vehicle-reg-input-label">{{ $t('vehicle_registration.choose_package') }}</label>
          <div class="form-element-wrapper">
            <sc-dropdown
              class="sc-vehicle-reg-package-selector"
              :options="packageOptions"
              :labelProp="packageLabelProp"
              :defaultItemIndex="packageDefaultItem"
              :dropdownQuantityProp="packageQuantityProp"
              v-on:select="catchSelectedPackage($event)"/>
          </div>
          <div class="error-message" v-if="isTouched('productPackage')">{{ errors.first('productPackage') }}</div>
        </sc-form-input>

        <input
          type="text"
          class="sc-vehicle-reg-not-displyed-input"
          name="productPackage"
          v-model="editedVehicleDetails.packageCode">

      </div>
    </sc-card>

    <div v-if="showModal">
      <sc-confirm-dialog
        v-bind:backdrop="true"
        v-bind:cancelLabel="modalCancelLabel"
        v-bind:okLabel="modalOkLabel"
        v-bind:closeOnBackdropClick="true"
        v-on:ok="catchOkResponse($event)"
        v-on:cancel="catchNoResponse($event)"
        v-on:close="catchCloseResponse($event)"
        class="confirm-dialog">
        <div slot="title">{{ $t('vehicle_registration.modal_title') }}</div>
        <div slot="text">{{ $t('vehicle_registration.modal_content') }}</div>
        <template v-slot:buttons="{ onConfirm, onReject }">
          <rounded-button color="outline" size="small" @click="onReject">{{ modalCancelLabel }}</rounded-button>
          <rounded-button class="ml-auto" color="primary" size="small" @click="onConfirm">{{ modalOkLabel }}</rounded-button>
        </template>
      </sc-confirm-dialog>
    </div>

    <rounded-button
      @click="saveVehicleDetails"
      color="primary"
      class="save-button">
        {{ $t('vehicle_registration.add_vehicle')}}
    </rounded-button>
  </div>

</template>

<script>
import { ScCard, ScFormInput, ScDropdown, ScConfirmDialog } from 'smartcommerce-component-library'
import { VehicleIcons } from '@/shared/utils/VehicleIcons'
import { PackageNameLabels, VehicleGroupLabels } from '@/shared/utils/Labels'
import validationMixin from '@/shared/mixins/validationMixin'
import { OrderWizardMutationTypes as mutations } from '../../store/mutation-types'
import VehicleDetails from '../../models/VehicleDetails'
import RoundedButton from '../../../shared/components/RoundedButton'

export default {
  name: 'RegisterVehiclesInput',
  components: {
    ScCard,
    ScDropdown,
    ScFormInput,
    ScConfirmDialog,
    RoundedButton
  },
  mixins: [validationMixin],
  data () {
    return {
      VehicleIcons,
      VehicleGroupLabels,
      currentYear: new Date().getFullYear(),
      packageLabelProp: 'label',
      packageQuantityProp: 'quantityText',
      packageDefaultItem: null,
      showModal: false,
      packageOptions: []
    }
  },
  props: {
    vehicleDetails: {
      type: Object
    }
  },
  methods: {
    onClickClose () {
      if (this.checkIfDataIsEmpty()) {
        this.$emit('closeForm')
      } else {
        this.showModal = true
      }
    },
    saveVehicleDetails () {
      this.touchAll()
      if (this.errors.any()) {
        return
      }
      this.$store.commit(mutations.STORE_EDITED_VEHICLE_DETAILS, this.editedVehicleDetails)
      this.$emit('closeForm')
      this.$ga && this.$ga.event({
        eventCategory: 'Registration',
        eventAction: 'Vehicle'
      })
    },
    checkIfDataIsEmpty () {
      return !this.editedVehicleDetails.vehicleBrand &&
             !this.editedVehicleDetails.vehicleType &&
             !this.editedVehicleDetails.licencePlate &&
             !this.editedVehicleDetails.productionYear &&
             !this.editedVehicleDetails.packageCode
    },
    catchSelectedPackage (event) {
      let previousPackageType = this.editedVehicleDetails.packageCode
      let selectedPackageType = event.packageCode
      if (previousPackageType === selectedPackageType) {
        return
      }
      this.editedVehicleDetails.packageCode = selectedPackageType

      for (let i = 0; i < this.packageOptions.length; i++) {
        const option = this.packageOptions[i]
        if (option.packageCode === selectedPackageType) {
          option.quantity--
        }
        if (previousPackageType && option.packageCode === previousPackageType) {
          option.quantity++
        }
      }
    },
    setPackageOptions () {
      let options = []
      const productGroupCode = this.editedVehicleDetails.groupCode
      const packagesInCart = this.$store.getters.getProductsInCartMappedByGroupAndPackage()[productGroupCode]
      const vehicleDetailsList = this.$store.getters.getVehiclesInCart()

      for (let p in packagesInCart) {
        const productPackage = packagesInCart[p][0]
        let numberOfPackagesLeft = productPackage.quantity - vehicleDetailsList.filter(v => v.isFilled() && v.packageCode === productPackage.code).length
        if (productPackage.code === this.editedVehicleDetails.packageCode) numberOfPackagesLeft++
        if (numberOfPackagesLeft === 0) continue
        options.push({
          label: this.$t('vehicle_registration.vehicle_type_and_package', {
            vehicleType: this.$t(VehicleGroupLabels[productGroupCode]),
            package: this.$t(PackageNameLabels[productPackage.code])
          }),
          quantityText: this.$t('vehicle_registration.packages_in_cart', {
            number: numberOfPackagesLeft
          }),
          packageCode: productPackage.code,
          disabled: false
        })
      }

      this.packageOptions = options
    },
    findDefaultPackageIndex () {
      if (this.editedVehicleDetails.packageCode) {
        for (let i = 0; i < this.packageOptions.length; i++) {
          if (this.editedVehicleDetails.packageCode === this.packageOptions[i].packageCode) {
            this.packageDefaultItem = i
            return
          }
        }
      } else {
        this.packageDefaultItem = 0
        this.catchSelectedPackage(this.packageOptions[0])
      }
    },
    catchOkResponse () {
      this.editedVehicleDetails.clear()
      this.$store.commit(mutations.STORE_EDITED_VEHICLE_DETAILS, this.editedVehicleDetails)
      this.$emit('closeForm')
    },
    catchNoResponse () {
      this.$emit('closeForm')
    },
    catchCloseResponse () {
      this.showModal = false
    },
    getPositionOfVehicleInGroup (details) {
      return this.$store.getters.getIndexOfVehicleInGroup(details) + 1
    },
    isFilled () {
      return new VehicleDetails(this.vehicleDetails).isFilled()
    }
  },
  computed: {
    modalCancelLabel () {
      return this.$t('vehicle_registration.modal_no_button')
    },
    modalOkLabel () {
      return this.$t('vehicle_registration.modal_ok_button')
    },
    editedVehicleDetails: {
      get () {
        return this.$store.state.editedVehicleDetails
      },
      set (val) {
        this.$store.commit(mutations.UPDATE_EDITED_VEHICLE_DETAILS_ITEM, val)
      }
    }
  },
  watch: {
    editedVehicleDetails: {
      handler: function () {
        this.$store.commit(mutations.UPDATE_EDITED_VEHICLE_DETAILS_ITEM, this.editedVehicleDetails)
      },
      deep: true
    }
  },
  mounted () {
    this.editedVehicleDetails = new VehicleDetails(JSON.parse(JSON.stringify(this.vehicleDetails)))
    this.setPackageOptions()
    this.findDefaultPackageIndex()
  }
}
</script>

<style lang="scss" scoped>
.sc-vehicle-reg-card {
  margin-top: 24px;
  width: 100%;
  padding: 0px 30px 20px 30px;
}
.sc-vehicle-reg-card-title {
  font-size: 18px;
  font-weight: 600;
  display: flex;
}
.sc-icon-cancel {
  color: #b50000;
  font-size: 32px;
  cursor: pointer;
  float: right;
}
.sc-vehicle-reg-card-title-and-icon {
  display: flex;
  align-items: flex-end;
}
.save-button {
  margin-top: 48px;
  width: 300px;
  margin-left: auto;
  margin-right: auto;
}
.sc-vehicle-reg-vehicle-icon {
  opacity: 0.5;
  display: flex;
  font-size: 36px;
  margin-right: 15px;
}
.sc-vehicle-reg-card-header-container {
  display: flex;
  justify-content: space-between;
}
.sc-vehicle-reg-choose-package {
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
  margin-top: 20px;
  margin-bottom: 5px;
}
.sc-vehicle-reg-make-type-licence {
  font-size: 18px;
  font-weight: bold;
}
.sc-vehicle-reg-not-displyed-input {
  visibility: hidden;
  position: absolute;
  z-index: -1;
  top: -9999px;
}
.sc-vehicle-reg-input-label {
  color: #455a64;
}
.sc-vehicle-reg-package-selector {
  width: 90%;
  float: left;
  margin-right: 20px;
}
.sc-vehicle-reg-upper-hr-line {
  border: none;
  height: 1px;
  background-color: #b0bec5;
  margin: 19px 0px 24px 0px;
}
.sc-vehicle-reg-bottom-hr-line {
  border: none;
  height: 1px;
  background-color: #e5e5e5;
  margin: 24px 0px;
}
::placeholder {
  opacity: 0.6;
}
</style>
